/**
 * Discuss dock for the mapping review — the same persistent-thread pattern as
 * the wiki and semantics docks: history is a real chat thread that survives a
 * refresh, turns stream over SSE, and the agent's tools edit the draft in place.
 *
 * Two sizes, one conversation: a compact panel in the review rail, and an
 * expanded overlay for when the exchange needs room. Whenever the agent reports
 * a mapping change, `onMappingChanged` refetches the draft so the grid behind
 * the dock catches up in the same beat.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { Maximize2, MessageCircle, Minimize2, Send } from 'lucide-react';
import MarkdownMessage from '../../components/chat/MarkdownMessage';
import Button from '../../components/ui/Button';
import { integrationsApi } from '../../services/integrations-api';
import type { ChatMessage, V2StreamEvent } from '@/types/chat';

interface DockMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Set when this turn changed the mapping — the grid moved because of it. */
  changedMapping: boolean;
}

const TOOL_LABEL: Record<string, string> = {
  get_mapping_draft: 'Reading the mapping…',
  update_mapping: 'Updating the mapping…',
  revise_mapping: 'Re-reading the file…',
};

const EMPTY_HINT =
  'Ask about any column, or tell me what to change — “what is Entree_pointee?”, “import the actual punch-in time too”, “rename Labor Cost to Wages”.';

function toDockMessage(m: ChatMessage): DockMessage {
  return {
    role: m.role === 'user' ? 'user' : 'assistant',
    text: m.segments
      .filter((s): s is Extract<typeof s, { type: 'text' }> => s.type === 'text')
      .map((s) => s.text)
      .join('\n\n'),
    // The payload persists on the tool segment, so a reloaded conversation
    // still shows which turns moved the mapping.
    changedMapping: m.segments.some(
      (s) =>
        s.type === 'tool' &&
        (s as { payload?: { type?: string } }).payload?.type === 'mapping_updated',
    ),
  };
}

interface Props {
  dataSourceId: string;
  /** Called after a turn in which the agent changed the draft. */
  onMappingChanged: () => void;
  /** Disables input while another write (the header inspector) is in flight. */
  disabled?: boolean;
  /** Mirrors streaming state up so approval can wait for a turn to land. */
  onBusyChange?: (busy: boolean) => void;
  /** The proposer's own summary, shown above an empty thread as context. */
  intro?: string;
  /** Seed the input from elsewhere (the inspector's "Ask Zamino instead"). */
  prefill?: { text: string; nonce: number } | null;
}

const InboundFileDiscussDock: React.FC<Props> = ({
  dataSourceId,
  onMappingChanged,
  disabled = false,
  onBusyChange,
  intro,
  prefill,
}) => {
  const [messages, setMessages] = useState<DockMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const hydratedFor = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: discussState, isLoading } = useQuery({
    queryKey: ['inbound-file-discuss', dataSourceId],
    queryFn: () => integrationsApi.getInboundFileDiscuss(dataSourceId),
  });

  // Hydrate from the persistent thread once per feed.
  useEffect(() => {
    if (!discussState || hydratedFor.current === dataSourceId) return;
    hydratedFor.current = dataSourceId;
    setMessages(
      discussState.messages.map(toDockMessage).filter((m) => m.text || m.changedMapping),
    );
  }, [discussState, dataSourceId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, working, expanded]);

  useEffect(() => {
    onBusyChange?.(streaming);
  }, [streaming, onBusyChange]);

  useEffect(() => {
    if (!prefill) return;
    setInput(prefill.text);
    inputRef.current?.focus();
  }, [prefill]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  const send = async (): Promise<void> => {
    const message = input.trim();
    if (!message || streaming || disabled) return;
    setInput('');
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'user', text: message, changedMapping: false }]);

    let assistantStarted = false;
    let mappingChanged = false;

    const appendDelta = (delta: string): void => {
      setMessages((prev) => {
        const next = [...prev];
        if (!assistantStarted || next[next.length - 1]?.role !== 'assistant') {
          assistantStarted = true;
          next.push({ role: 'assistant', text: delta, changedMapping: false });
        } else {
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, text: last.text + delta };
        }
        return next;
      });
    };

    const markChanged = (): void => {
      mappingChanged = true;
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          next[next.length - 1] = { ...last, changedMapping: true };
        } else {
          assistantStarted = true;
          next.push({ role: 'assistant', text: '', changedMapping: true });
        }
        return next;
      });
      onMappingChanged();
    };

    const onEvent = (event: V2StreamEvent): void => {
      switch (event.type) {
        case 'text_delta':
          setWorking(null);
          appendDelta(event.delta as string);
          break;
        case 'tool_start':
          setWorking(TOOL_LABEL[event.toolName as string] ?? 'Working…');
          break;
        case 'tool_result': {
          setWorking(null);
          const payload = (event as { payload?: { type?: string } }).payload;
          if (event.success && payload?.type === 'mapping_updated') markChanged();
          break;
        }
        case 'error':
          toast.error((event.error as string) || 'Something went wrong');
          break;
        default:
          break;
      }
    };

    try {
      await integrationsApi.discussInboundFile(dataSourceId, message, onEvent);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reach Zamino');
    } finally {
      setStreaming(false);
      setWorking(null);
      // The final refresh covers a turn whose tool events we may have missed.
      if (mappingChanged) onMappingChanged();
    }
  };

  const busy = streaming || disabled;

  const conversation = (
    <>
      <div
        ref={scrollRef}
        className={clsx(
          'flex flex-1 flex-col gap-2 overflow-y-auto p-2.5',
          expanded && 'gap-3 p-4',
        )}
      >
        {isLoading && <p className="text-xs text-muted-foreground">Loading the conversation…</p>}
        {!isLoading && messages.length === 0 && !streaming && (
          <div className="my-auto space-y-2 px-2 text-xs text-muted-foreground">
            {intro && (
              <p className="rounded-lg border border-border bg-muted px-2.5 py-2 text-left text-foreground">
                {intro}
              </p>
            )}
            <p className="text-center">{EMPTY_HINT}</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={clsx(
              'max-w-[92%] whitespace-pre-wrap rounded-xl px-2.5 py-1.5 text-xs leading-relaxed',
              m.role === 'user'
                ? 'self-end rounded-br-sm bg-primary text-primary-foreground'
                : 'self-start rounded-bl-sm bg-muted text-foreground',
            )}
          >
            {m.role === 'assistant' ? <MarkdownMessage content={m.text} /> : m.text}
            {m.changedMapping && (
              <p className="mt-1 text-[10.5px] font-bold text-success">✓ Mapping updated</p>
            )}
          </div>
        ))}
        {working && <p className="self-start text-xs text-muted-foreground">{working}</p>}
      </div>

      <div className={clsx('flex gap-1.5 border-t border-border p-2', expanded && 'p-3')}>
        <input
          ref={inputRef}
          type="text"
          className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
          placeholder={disabled ? 'Finishing the last change…' : 'Tell me what to change…'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
          disabled={busy}
        />
        <Button size="sm" onClick={() => void send()} disabled={!input.trim() || busy}>
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </>
  );

  const header = (
    <div className="flex items-center justify-between gap-2 border-b border-border bg-muted px-3 py-2 text-xs font-semibold">
      <span className="flex items-center gap-2">
        <MessageCircle className="h-3.5 w-3.5" />
        Discuss with Zamino
      </span>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={expanded ? 'Collapse the conversation' : 'Expand the conversation'}
        className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>
    </div>
  );

  if (expanded) {
    return (
      <>
        <div
          className="fixed inset-0 z-40 bg-foreground/20"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-label="Discuss the mapping"
          className="fixed left-1/2 top-1/2 z-50 flex h-[min(640px,85vh)] w-[min(680px,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        >
          {header}
          {conversation}
        </div>
      </>
    );
  }

  return (
    <div className="flex max-h-[340px] min-h-[220px] flex-col overflow-hidden rounded-lg border border-border bg-card">
      {header}
      {conversation}
    </div>
  );
};

export default InboundFileDiscussDock;
